import { Image, Upload, X } from "lucide-react";
import { type DragEvent, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SearchInput } from "@/components/ui/search-input";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { InlineSvg } from "./inline-svg";
import { T } from "./svg-icon-field.i18n";

/** Max icons rendered in the picker grid at once. */
const MAX_GRID_ICONS = 200;

/** One entry of lucide-static's icon-nodes.json: [tag, attributes]. */
type LucideIconNode = [tag: string, attrs: Record<string, string>];

/** Root <svg> attributes lucide uses for every icon (kept monochrome via currentColor). */
const LUCIDE_SVG_ATTRS =
  'xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';

/** Build the raw SVG markup string for one icon from its node list. */
function lucideNodesToSvg(nodes: LucideIconNode[]): string {
  const children = nodes
    .map(([tag, attrs]) => {
      const attrStr = Object.entries(attrs)
        .map(([key, value]) => ` ${key}="${value}"`)
        .join("");
      return `<${tag}${attrStr} />`;
    })
    .join("");
  return `<svg ${LUCIDE_SVG_ATTRS}>${children}</svg>`;
}

type LucideIcon = { name: string; svg: string };

// Fetched at most once per session (also dedupes StrictMode double-effects).
let lucideIconsPromise: Promise<LucideIcon[]> | null = null;

function loadLucideIcons(): Promise<LucideIcon[]> {
  // Served by the API (GET /public/lucide-icons), not a static file: the web dev
  // server (`bun index.html`) returns the SPA shell for every non-bundled path,
  // so a `/public/*.json` URL yields index.html, not the manifest. The API serves
  // it identically in dev and prod and keeps the ~700 KB manifest out of the JS
  // bundle.
  lucideIconsPromise ??= fetch(`${process.env.BUN_PUBLIC_API_URL ?? ""}/public/lucide-icons`)
    .then((res) => {
      if (!res.ok) throw new Error(`icon manifest fetch failed: ${res.status}`);
      return res.json() as Promise<Record<string, LucideIconNode[]>>;
    })
    .then((manifest) => {
      // Names are already kebab-case and alias-free in icon-nodes.json.
      const loaded = Object.entries(manifest).map(([name, nodes]) => ({
        name,
        svg: lucideNodesToSvg(nodes),
      }));
      loaded.sort((a, b) => a.name.localeCompare(b.name));
      return loaded;
    })
    .catch((err) => {
      // Allow a retry on the next open instead of caching the failure.
      lucideIconsPromise = null;
      throw err;
    });
  return lucideIconsPromise;
}

/**
 * Icon picker dialog backed by the Lucide icon set (`lucide-static`).
 * The icon manifest is a content-hashed asset fetched only when the dialog
 * mounts, so the full icon set never lands in the main bundle.
 */
function LucideIconPickerDialog({
  open,
  onOpenChange,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (svg: string) => void;
}) {
  const t = useT(T);

  const [icons, setIcons] = useState<LucideIcon[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || icons) return;
    let cancelled = false;
    // Lazy-load the Lucide manifest — keeps lucide-static out of the main bundle.
    loadLucideIcons()
      .then((loaded) => {
        if (!cancelled) setIcons(loaded);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, icons]);

  // Reset search each time the picker opens
  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const filtered = useMemo(() => {
    if (!icons) return [];
    const q = search.trim().toLowerCase();
    return q ? icons.filter((icon) => icon.name.includes(q)) : icons;
  }, [icons, search]);

  const shown = filtered.slice(0, MAX_GRID_ICONS);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>{t.pickerTitle}</DialogTitle>
        </DialogHeader>

        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder={t.searchPlaceholder}
          autoFocus
        />

        {loadFailed ? (
          <div className="py-12 text-center text-sm text-destructive">{t.loadFailed}</div>
        ) : !icons ? (
          <div className="py-12 text-center text-sm text-muted-foreground">{t.loadingIcons}</div>
        ) : shown.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">{t.noMatches}</div>
        ) : (
          <div className="grid max-h-[360px] grid-cols-10 gap-1 overflow-y-auto p-1">
            {shown.map((icon) => (
              // 200 cells, so no Tooltip per cell — that is 200 Radix instances
              // for a hint the grid barely needs. The name is on `aria-label`,
              // which is what the icon actually lacked.
              <InlineSvg
                as="button"
                key={icon.name}
                type="button"
                aria-hidden={false}
                aria-label={icon.name}
                onClick={() => {
                  onPick(icon.svg);
                  onOpenChange(false);
                }}
                className="flex h-11 items-center justify-center rounded-md border border-transparent text-foreground transition-colors hover:border-border hover:bg-accent [&_svg]:h-5 [&_svg]:w-5"
                svg={icon.svg}
              />
            ))}
          </div>
        )}

        {icons && filtered.length > MAX_GRID_ICONS && (
          <p className="text-xs text-muted-foreground">
            {t.showingOf(shown.length, filtered.length)}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

type SvgIconFieldProps = {
  id?: string;
  /** Raw SVG markup string, or empty when unset. */
  value: string;
  onChange: (svg: string) => void;
  disabled?: boolean;
};

/**
 * Shared icon input — the value is a raw SVG markup string (same storage
 * contract as the old markup textareas). Admins pick an icon from the Lucide
 * library or upload an .svg file; hand-pasting markup is no longer offered.
 */
export function SvgIconField({ id, value, onChange, disabled }: SvgIconFieldProps) {
  const t = useT(T);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    try {
      const text = (await file.text()).trim();
      if (!text.includes("<svg")) {
        setUploadError(t.invalidSvg);
        return;
      }
      setUploadError(null);
      onChange(text);
    } catch {
      setUploadError(t.readFailed);
    }
  };

  const hasIcon = Boolean(value.trim());

  // Drag-and-drop is pointer-only by nature, so the tile stays aria-hidden and
  // the "Upload SVG" button remains the keyboard path to the same handler.
  const dropHandlers = disabled
    ? {}
    : {
        onDragOver: (e: DragEvent) => {
          e.preventDefault();
          setIsDragging(true);
        },
        onDragLeave: () => setIsDragging(false),
        onDrop: (e: DragEvent) => {
          e.preventDefault();
          setIsDragging(false);
          const file = e.dataTransfer.files?.[0];
          if (file) void handleFile(file);
        },
      };

  const tileProps = {
    "aria-hidden": true,
    ...dropHandlers,
    className: cn(
      // `pointer-events-none` on the glyph keeps every drag event targeted at
      // the tile itself. Without it, moving onto the rendered icon fires
      // `dragleave` on the tile and the highlight blinks off until the next
      // `dragover` bubbles back up. Same idiom `Button` uses for its icons.
      "flex h-10 w-10 shrink-0 items-center justify-center rounded-md transition-colors [&_svg]:pointer-events-none [&_svg]:h-6 [&_svg]:w-6",
      // Flat, unelevated, and a size the h-8 buttons do not share: a swatch
      // showing the current value, not a fourth control in the row.
      hasIcon ? "bg-muted text-foreground" : "border border-dashed border-input",
      // Brand orange is the app's active-state accent (DESIGN.md › Colors), and
      // it is tenant-overridable, so the drop target lights up in the tenant's
      // own colour instead of the near-black `primary`.
      isDragging && "border border-dashed border-brand bg-brand/10 text-brand",
      uploadError && "border border-dashed border-destructive text-destructive",
    ),
  };

  return (
    <div className="space-y-2">
      {/* Value first, then the controls: the preview and its clear button, then
          the two ways to set a new icon. All packed left, like every other
          control in the forms this field appears in.

          The preview is flat, unelevated and a size the h-8 buttons do not
          share, because styled like the outline buttons it sat next to it read
          as a fourth control that did nothing when clicked. Dropping an .svg on
          it works, which is what the dashed empty state has always implied. */}
      <div className="flex items-center gap-2">
        {hasIcon ? (
          <InlineSvg as="div" {...tileProps} svg={value} />
        ) : (
          <div {...tileProps}>
            {uploadError ? (
              <X className="h-4 w-4 text-destructive" />
            ) : (
              <Image
                className={cn("h-4 w-4", isDragging ? "text-brand" : "text-muted-foreground/50")}
              />
            )}
          </div>
        )}

        {hasIcon && (
          <Button
            type="button"
            size="icon"
            tooltip={t.remove}
            variant="ghost"
            aria-label={t.clearIcon}
            onClick={() => {
              setUploadError(null);
              onChange("");
            }}
            disabled={disabled}
          >
            <X className="h-4 w-4" />
          </Button>
        )}

        {/* A little extra air marks the seam between the value and the two
            controls that replace it, now that nothing else separates them. */}
        <div className="ml-1 flex items-center gap-2">
          <Button
            id={id}
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setPickerOpen(true)}
            disabled={disabled}
          >
            {t.chooseIcon}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
          >
            <Upload className="mr-1.5 h-3.5 w-3.5" />
            {t.uploadSvg}
          </Button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".svg,image/svg+xml"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            // Allow re-selecting the same file
            e.target.value = "";
          }}
        />
      </div>

      {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}

      {pickerOpen && (
        <LucideIconPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onPick={(svg) => {
            setUploadError(null);
            onChange(svg);
          }}
        />
      )}
    </div>
  );
}
